import React from "react";
import { Helmet } from "react-helmet-async";
import { ChevronDown, MessageSquare } from "lucide-react";
import { Link } from "react-router-dom";

const FaqDivers = ({ setOpenIndex, openIndex, toggleFAQ, idCategorie }) => {
  const faqList = [
    {
      question: () => (
        <strong>
          1. Pourquoi utiliser la catégorie "Autres" sur Deal.mg ?
        </strong>
      ),
      answer: () => (
        <p>
          Cette catégorie est parfaite pour les objets uniques, insolites ou
          très spécifiques qui ne correspondent pas aux catégories classiques
          comme l'immobilier ou les véhicules.
        </p>
      ),
    },
    {
      question: () => (
        <strong>
          2. Comment acheter des articles divers en toute sécurité sur Deal.mg ?
        </strong>
      ),
      answer: () => (
        <p>
          Pour acquérir des{" "}
          <strong>objets de collection ou fournitures diverses</strong> sur{" "}
          <strong>Deal.mg</strong>, demandez des photos haute définition et des
          précisions sur l'authenticité de l'article par téléphone.
          S'agissantsouvent de pièces uniques, convenez d'un mode d'expédition
          sécurisé par transporteur pour les achats hors de votre ville. Un
          échange direct avec le vendeur reste la méthode la plus efficace pour
          valider la rareté et l'état de ces trésors avant de finaliser votre
          transaction à Madagascar.
        </p>
      ),
    },
    {
      question: () => (
        <strong>3. Comment vendre mes objets divers rapidement ?</strong>
      ),
      answer: () => (
        <p>
          Prenez des photos claires sous plusieurs angles et rédigez une
          description très précise de l'objet, de sa matière et de son état.
          L'utilisation de notre outil d'IA aide à générer une description
          optimisée pour le <strong>SEO divers.</strong>
        </p>
      ),
    },
    {
      question: () => (
        <strong>4. Puis-je trouver des antiquités malgaches ?</strong>
      ),
      answer: () => (
        <p>
          Oui, de nombreux utilisateurs revendent des objets artisanaux
          traditionnels ou des meubles anciens. Utilisez la barre de recherche
          pour trouver des pièces spécifiques.
        </p>
      ),
    },
    {
      question: () => (
        <strong>
          5. Comment vérifier l'authenticité d'un objet de collection ?
        </strong>
      ),
      answer: () => (
        <p>
          Pour les objets de valeur, demandez la facture d'origine, un
          certificat d'authenticité ou faites appel à un expert si nécessaire.
        </p>
      ),
    },
    {
      question: () => (
        <strong>
          6. Comment fixer le juste prix pour mes objets à vendre ?
        </strong>
      ),
      answer: () => (
        <p>
          Comme il s'agit d'objets uniques, fixez un prix basé sur leur rareté,
          leur état et la demande pour des{" "}
          <strong>objets de collection à Madagascar.</strong>
        </p>
      ),
    },
    {
      question: () => (
        <strong>
          7. Existe-t-il des risques de contrefaçon dans cette catégorie ?
        </strong>
      ),
      answer: () => (
        <p>
          La vigilance est de mise pour les articles de marque ou de grande
          valeur. Demandez des preuves d'origine pour les objets qui semblent
          être des répliques.
        </p>
      ),
    },
    {
      question: () => <strong>8. Comment signaler un objet suspect ?</strong>,
      answer: () => (
        <p>
          Si vous détectez une annonce frauduleuse ou un objet dangereux,
          cliquez sur le bouton « Signaler » pour alerter notre équipe de
          modération.
        </p>
      ),
    },
    {
      question: () => (
        <strong>9. Puis-je vendre des objets dangereux ou illégaux ?</strong>
      ),
      answer: () => (
        <p>
          Non, la vente d'armes, de stupéfiants ou d'objets dangereux est
          strictement interdite sur Deal.mg. Consultez nos règles de publication
          avant de poster.
        </p>
      ),
    },
    {
      question: () => (
        <strong>
          10. Comment Deal.mg assure-t-il la sécurité pour les objets rares ou
          de collection ?
        </strong>
      ),
      answer: () => (
        <p>
          Deal.mg facilite la mise en relation sécurisée. Pour les objets
          uniques ou de collection, nous recommandons de{" "}
          <strong>vérifier l'authenticité de l'objet en personne</strong> et de
          finaliser la vente de gré à gré après inspection.
        </p>
      ),
    },
  ];

  return (
    <>
      <Helmet>
        <title>
          Petites annonces et objets insolites à Madagascar | Deal.mg
        </title>
        <meta
          name="description"
          content="Découvrez les objets insolites, de collection et diverses annonces d'occasion à Madagascar sur Deal.mg."
        />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org/",
            "@type": "Product",
            name: "Petites annonces diverses et objets insolites à Madagascar",
            image: ["https://deal.mg/logo.png"],
            description:
              "Objets uniques, antiquités et fournitures diverses d'occasion à Madagascar sur Deal.mg",
            sku: idCategorie,
            offers: {
              "@type": "Offer",
              url: `https://deal.mg/article/resultat?idCategorie=${idCategorie}`,
              availability: "https://schema.org/InStock",
            },
          })}
        </script>
      </Helmet>

      <h1 className="text-center">
        Petites annonces diverses et objets insolites à Madagascar
      </h1>

      <p>
        Découvrez des trésors cachés dans la catégorie <strong>Autres</strong>{" "}
        de Deal.mg, le coin des bonnes affaires insolites et des objets de
        collection à <strong>Madagascar</strong>. Vous cherchez quelque chose
        d'unique qui ne rentre pas dans les catégories classiques ? C'est ici
        que vous trouverez des perles rares, des objets de décoration, des
        fournitures spécifiques ou des objets occasion introuvables ailleurs, à{" "}
        <strong>Antananarivo, Toamasina</strong> et dans tout Madagascar. En
        publiant vos annonces dans cette section, vous ciblez des passionnés à
        la recherche de <strong>bonnes affaires à Madagascar.</strong>
      </p>

      <h2>Objets de collection et antiquités</h2>
      <p>
        Passionnés d'histoire ou collectionneurs, découvrez des pièces uniques :
        vieilles pièces de monnaie, timbres rares, meubles anciens ou objets
        artisanaux traditionnels malgaches. La{" "}
        <strong>chasse aux trésors à Madagascar</strong>{" "}
        commence sur Deal.mg.
      </p>

      <h2>Fournitures et objets divers</h2>
      <p>
        Vous avez besoin d'équipements spécifiques pour un projet ? Trouvez des
        matériaux de bricolage, des fournitures de bureau, ou des{" "}
        <Link to="/maison_et_jardin/tous?categorie=5">
          <strong>objets divers occasion</strong>
        </Link>{" "}
        qui facilitent votre quotidien. Cette catégorie est idéale pour recycler
        et donner une seconde vie à des articles utilitaires.
      </p>

      <h2>Transformez vos trésors cachés en espèces</h2>
      <p>
        Vous avez des objets uniques qui encombrent votre garage ?
        Transformez-les en argent. Publiez une annonce détaillée en utilisant
        notre outil d'IA pour optimiser votre description et garantir une{" "}
        <Link>
          <strong>vente rapide</strong>
        </Link>{" "}
        auprès d'un public ciblé. N'hésitez pas à jeter un œil à la{" "}
        <Link to="/electronique/tous?categorie=6">
          <strong>Électronique</strong>
        </Link>{" "}
        pour trouver des appareils photo afin de mettre en valeur vos objets à
        vendre.
      </p>

      <br />

      <h2 className="support_faq_title">
        <MessageSquare size={20} className="support_faq_title_icon" />
        FAQ
      </h2>

      {faqList.map((item, index) => (
        <div
          key={index}
          className={`support_faq_item ${openIndex === index ? "open" : ""}`}
        >
          <div
            className="support_faq_question"
            onClick={() => toggleFAQ(index)}
          >
            <span>{item.question()}</span>
            <ChevronDown
              className={`support_faq_arrow ${
                openIndex === index ? "support_faq_rotate_180" : ""
              }`}
              size={20}
            />
          </div>

          <div
            className={`support_faq_answer_container${openIndex === index ? "active" : ""}`}
          >
            <div className="support_faq_answer">{item.answer()}</div>
          </div>
        </div>
      ))}
    </>
  );
};

export default FaqDivers;
